import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Slider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Phone as CallIcon,
  CallEnd as HangUpIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Dialpad as DialpadIcon,
  Settings as SettingsIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { useWebPhoneContext } from '../../contexts/WebPhoneContext';

interface WebPhoneWindowProps {
  onClose: () => void;
  onMinimize: () => void;
  isMinimized: boolean;
}

const WebPhoneWindow: React.FC<WebPhoneWindowProps> = ({ onClose, onMinimize, isMinimized }) => {
  const {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    audioDevices,
    selectedSpeaker,
    selectedMicrophone,
    volume,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    addDigit,
    clearNumber,
    changeSpeaker,
    changeMicrophone,
    changeVolume,
    testSpeaker,
    restoreWindow,
  } = useWebPhoneContext();

  const [position, setPosition] = useState({ x: window.innerWidth - 360, y: 80 });
  const [minimizedPosition, setMinimizedPosition] = useState({ x: window.innerWidth - 240, y: window.innerHeight - 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showDialpad, setShowDialpad] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  const windowRef = useRef<HTMLDivElement>(null);

  // Dragging functionality
  const handleMouseDown = (e: React.MouseEvent, isMinimizedWidget = false) => {
    if ((e.target as HTMLElement).closest('.phone-controls')) {
      return; // Don't drag when clicking buttons
    }

    setIsDragging(true);
    const currentPos = isMinimizedWidget ? minimizedPosition : position;
    setDragOffset({
      x: e.clientX - currentPos.x,
      y: e.clientY - currentPos.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newPos = {
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        };
        if (isMinimized) {
          setMinimizedPosition(newPos);
        } else {
          setPosition(newPos);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, isMinimized]);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusDisplay = () => {
    if (connectionStatus === 'disconnected') return { text: '🔴 Disconnected', color: '#f44336' };
    if (connectionStatus === 'connecting') return { text: '🟡 Connecting...', color: '#ff9800' };
    if (connectionStatus === 'connected') return { text: '🟢 Connected', color: '#4caf50' };
    if (connectionStatus === 'registered') return { text: '🟢 Ready', color: '#4caf50' };
    if (connectionStatus === 'error') return { text: '🔴 Error', color: '#f44336' };
    return { text: 'Unknown', color: '#9e9e9e' };
  };

  const getCallStatusDisplay = () => {
    if (callStatus === 'calling') return { text: '📞 Calling...', color: '#ff9800' };
    if (callStatus === 'ringing') return { text: '📞 Ringing...', color: '#ff9800' };
    if (callStatus === 'in-call') return { text: '☎️ In Call', color: '#2196f3' };
    return null;
  };

  const status = getStatusDisplay();
  const callStatusInfo = getCallStatusDisplay();

  // Show compact minimized view
  if (isMinimized) {
    const handleMinimizedClick = () => {
      if (!isDragging) {
        restoreWindow();
      }
    };

    return (
      <Paper
        onClick={handleMinimizedClick}
        onMouseDown={(e) => handleMouseDown(e, true)}
        sx={{
          position: 'fixed',
          left: minimizedPosition.x,
          top: minimizedPosition.y,
          width: 200,
          zIndex: 2000,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'all 0.2s',
          '&:hover': {
            transform: isDragging ? 'none' : 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            userSelect: 'none',
          }}
        >
          <CallIcon />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              WebPhone
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {callStatusInfo ? callStatusInfo.text : status.text}
            </Typography>
          </Box>
          <IconButton
            size="small"
            className="phone-controls"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {currentCall && (
          <Box sx={{ p: 1.5, bgcolor: 'background.paper', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {currentCall.remoteNumber}
            </Typography>
            {currentCall.status === 'connected' && (
              <Typography variant="caption" color="text.secondary">
                {formatDuration(currentCall.duration)}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  // Dialpad keys
  const dialpadKeys = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
  ];

  return (
    <Paper
      ref={windowRef}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: 320,
        zIndex: 2000,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      {/* Header */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            WebPhone
          </Typography>
        </Box>
        <Box className="phone-controls" sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('[WebPhoneWindow] Minimize button clicked');
              onMinimize();
            }}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('[WebPhoneWindow] Close button clicked');
              onClose();
            }}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {/* Status Display */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <Chip
            label={callStatusInfo ? callStatusInfo.text : status.text}
            size="small"
            sx={{
              flex: 1,
              fontWeight: 600,
              bgcolor: callStatusInfo ? callStatusInfo.color : status.color,
              color: 'white',
            }}
          />
          {/* Always show settings button */}
          <IconButton
            size="small"
            onClick={() => setShowSettings(!showSettings)}
            sx={{
              bgcolor: showSettings ? '#667eea' : '#f8f9fa',
              color: showSettings ? 'white' : '#666',
              '&:hover': { bgcolor: showSettings ? '#764ba2' : '#e9ecef' },
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Audio Device Settings */}
        <Collapse in={showSettings}>
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Audio Devices
            </Typography>

            {/* Speaker Selection - always show with test button */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                  <InputLabel id="speaker-select-label">Speaker</InputLabel>
                  <Select
                    labelId="speaker-select-label"
                    value={selectedSpeaker || 'default'}
                    label="Speaker"
                    onChange={(e) => changeSpeaker(e.target.value)}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="default">System Default</MenuItem>
                    {audioDevices.outputDevices.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={testSpeaker}
                  sx={{
                    minWidth: 40,
                    px: 1,
                    bgcolor: 'white',
                    '&:hover': { bgcolor: '#f8f9fa' }
                  }}
                  title="Test speaker"
                >
                  <VolumeUpIcon fontSize="small" />
                </Button>
              </Box>
            </Box>

            {/* Microphone Selection - always show */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="microphone-select-label">Microphone</InputLabel>
              <Select
                labelId="microphone-select-label"
                value={selectedMicrophone || 'default'}
                label="Microphone"
                onChange={(e) => changeMicrophone(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="default">System Default</MenuItem>
                {audioDevices.inputDevices.map((device) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Volume Control */}
            <Box sx={{ px: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
                Volume: {volume}%
              </Typography>
              <Slider
                value={volume}
                onChange={(_, newValue) => changeVolume(newValue as number)}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                sx={{
                  color: '#667eea',
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                  },
                }}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Error Message */}
        {errorMessage && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#fee', borderRadius: 1 }}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Volume Control - Always visible */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
            Volume: {volume}%
          </Typography>
          <Slider
            value={volume}
            onChange={(_, newValue) => changeVolume(newValue as number)}
            min={0}
            max={100}
            step={5}
            size="small"
            sx={{
              color: '#667eea',
              mb: 1,
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={testSpeaker}
            fullWidth
            startIcon={<VolumeUpIcon fontSize="small" />}
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              textTransform: 'none',
            }}
          >
            Test Speaker
          </Button>
        </Box>

        {/* Call Info (when in call) */}
        {currentCall && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentCall.remoteNumber}
            </Typography>
            {currentCall.status === 'connected' && (
              <Typography variant="body2" color="text.secondary">
                {formatDuration(currentCall.duration)}
              </Typography>
            )}
          </Box>
        )}

        {/* Number Display */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            minHeight: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            bgcolor: '#f8f9fa',
            borderColor: '#e9ecef',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: dialedNumber ? '#333' : '#adb5bd',
              wordBreak: 'break-all',
            }}
          >
            {dialedNumber || 'Enter number'}
          </Typography>
        </Paper>

        {/* Dialpad (show only when not in call or if toggled) */}
        {(callStatus === 'idle' || showDialpad) && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {dialpadKeys.map((key) => (
              <Grid item xs={4} key={key.digit}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => addDigit(key.digit)}
                  sx={{
                    height: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: '#f8f9fa',
                    borderColor: '#e9ecef',
                    '&:hover': {
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    {key.digit}
                  </Typography>
                  {key.letters && (
                    <Typography variant="caption" sx={{ color: '#666', fontSize: 10 }}>
                      {key.letters}
                    </Typography>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid container spacing={1}>
          {callStatus === 'idle' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={() => makeCall(dialedNumber)}
                  disabled={connectionStatus !== 'registered' || !dialedNumber || callStatus !== 'idle'}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#45a049' },
                  }}
                >
                  Call
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BackspaceIcon />}
                  onClick={clearNumber}
                >
                  Clear
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'calling' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'incoming' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={answerCall}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Answer
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Decline
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'in-call' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                  sx={{
                    bgcolor: isMuted ? '#ff9800' : 'transparent',
                    color: isMuted ? 'white' : 'inherit',
                  }}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isOnHold ? <PlayIcon /> : <PauseIcon />}
                  onClick={toggleHold}
                  sx={{
                    bgcolor: isOnHold ? '#9c27b0' : 'transparent',
                    color: isOnHold ? 'white' : 'inherit',
                  }}
                >
                  {isOnHold ? 'Resume' : 'Hold'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DialpadIcon />}
                  onClick={() => setShowDialpad(!showDialpad)}
                >
                  {showDialpad ? 'Hide' : 'Show'} Keys
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Hang Up
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Paper>
  );
};

export default WebPhoneWindow;
